import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { categoryActions } from '../../action/categoryaction';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';

const EditCategory = ({ onSuccess }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [loadingSubcategories, setLoadingSubcategories] = useState(false);
    const [parentCategories, setParentCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
    const [newSubcategory, setNewSubcategory] = useState({
        category_name: '',
        description: '',
        status: 'Active'
    });
    const [categoryData, setCategoryData] = useState({
        id: '',
        category_name: '',
        description: '',
        parent_id: null,
        status: 'Active'
    });
    const [errors, setErrors] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Get selected category from Redux store
    const selectedCategory = useSelector((state) => state.selected_category || {});

    // Fetch parent categories for dropdown
    useEffect(() => {
        const fetchParentCategories = async () => {
            try {
                const response = await dispatch(categoryActions.fetchParentCategories());
                if (response.success && response.data) {
                    const options = response.data.parent_categories?.map(cat => ({
                        value: cat.id,
                        label: cat.category_name
                    })) || [];
                    
                    // Filter out current category from parent options to prevent circular reference
                    const filteredOptions = options.filter(opt => opt.value !== categoryData.id);
                    setParentCategories(filteredOptions);
                }
            } catch (error) {
                console.error('Error fetching parent categories:', error);
            }
        };
        
        if (categoryData.id) {
            fetchParentCategories();
        }
    }, [dispatch, categoryData.id]);

    // Load subcategories when category is selected
    useEffect(() => {
        if (selectedCategory && selectedCategory.id) {
            loadSubcategories(selectedCategory.id);
        }
    }, [selectedCategory]);

    const loadSubcategories = async (categoryId) => {
        if (!categoryId) return;
        
        setLoadingSubcategories(true);
        try {
            // Fetch subcategories using the fetchCategories action with parent_id filter
            const result = await dispatch(categoryActions.fetchCategories({
                parent_id: categoryId,
                page: 1,
                limit: 100
            }));
            
            if (result.success) {
                setSubcategories(result.data.categories || []);
            }
        } catch (error) {
            console.error('Error loading subcategories:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to load subcategories',
                confirmButtonColor: '#ff0000'
            });
        } finally {
            setLoadingSubcategories(false);
        }
    };

    // Initialize form when selectedCategory changes
    useEffect(() => {
        if (selectedCategory && selectedCategory.id) {
            setCategoryData({
                id: selectedCategory.id,
                category_name: selectedCategory.category_name || selectedCategory.Brand || '',
                description: selectedCategory.description || '',
                parent_id: selectedCategory.parent_id || null,
                status: selectedCategory.status || selectedCategory.Status || 'Active'
            });
            setIsModalOpen(true);
        }
    }, [selectedCategory]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCategoryData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleParentChange = (selectedOption) => {
        setCategoryData(prev => ({
            ...prev,
            parent_id: selectedOption ? selectedOption.value : null
        }));
        
        if (errors.parent_id) {
            setErrors(prev => ({
                ...prev,
                parent_id: ''
            }));
        }
    };

    const handleStatusChange = (e) => {
        setCategoryData(prev => ({
            ...prev,
            status: e.target.checked ? 'Active' : 'InActive'
        }));
    };

    const handleSubcategoryInputChange = (e) => {
        const { name, value } = e.target;
        setNewSubcategory(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubcategoryStatusChange = (e) => {
        setNewSubcategory(prev => ({
            ...prev,
            status: e.target.checked ? 'Active' : 'InActive'
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!categoryData.category_name.trim()) {
            newErrors.category_name = 'Category name is required';
        } else if (categoryData.category_name.length < 2) {
            newErrors.category_name = 'Category name must be at least 2 characters';
        } else if (categoryData.category_name.length > 100) {
            newErrors.category_name = 'Category name cannot exceed 100 characters';
        }
        
        // Check for circular reference (category cannot be its own parent)
        if (categoryData.parent_id === categoryData.id) {
            newErrors.parent_id = 'Category cannot be its own parent';
        }
        
        if (categoryData.description && categoryData.description.length > 500) {
            newErrors.description = 'Description cannot exceed 500 characters';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        try {
            const response = await dispatch(categoryActions.updateCategory(categoryData.id, categoryData));
            
            if (response.success) {
                // Show success message
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Category updated successfully',
                    confirmButtonColor: '#00ff00',
                    timer: 2000
                });
                
                // Refresh category list
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error('Error updating category:', error);
        } finally {
            setLoading(false);
        }
    };

    // Add new subcategory
    const handleAddSubcategory = async (e) => {
        e.preventDefault();
        
        if (!newSubcategory.category_name.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Subcategory name is required',
                confirmButtonColor: '#ffa500'
            });
            return;
        }
        
        try {
            const subcategoryData = {
                category_name: newSubcategory.category_name,
                description: newSubcategory.description,
                parent_id: categoryData.id,
                status: newSubcategory.status,
                created_by: 1 // Default admin user ID
            };
            
            const response = await dispatch(categoryActions.createCategory(subcategoryData));
            
            if (response.success) {
                // Reset subcategory form
                setNewSubcategory({
                    category_name: '',
                    description: '',
                    status: 'Active'
                });
                setShowSubcategoryForm(false);
                
                // Reload subcategories
                await loadSubcategories(categoryData.id);
                
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Subcategory added successfully',
                    confirmButtonColor: '#00ff00',
                    timer: 1500
                });
            }
        } catch (error) {
            console.error('Error adding subcategory:', error);
        }
    };

    // Edit subcategory
    const handleEditSubcategory = (subcategory) => {
        // Open a modal or form to edit subcategory
        // For now, we'll show a prompt
        Swal.fire({
            title: 'Edit Subcategory',
            html: `
                <input type="text" id="subcategory-name" class="swal2-input" placeholder="Subcategory Name" value="${subcategory.category_name}">
                <textarea id="subcategory-description" class="swal2-textarea" placeholder="Description">${subcategory.description || ''}</textarea>
                <select id="subcategory-status" class="swal2-select">
                    <option value="Active" ${subcategory.status === 'Active' ? 'selected' : ''}>Active</option>
                    <option value="InActive" ${subcategory.status === 'InActive' ? 'selected' : ''}>InActive</option>
                </select>
            `,
            showCancelButton: true,
            confirmButtonText: 'Update',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const name = document.getElementById('subcategory-name').value;
                const description = document.getElementById('subcategory-description').value;
                const status = document.getElementById('subcategory-status').value;
                
                if (!name.trim()) {
                    Swal.showValidationMessage('Subcategory name is required');
                    return false;
                }
                
                return { name, description, status };
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                const { name, description, status } = result.value;
                
                const updateData = {
                    category_name: name,
                    description: description,
                    status: status,
                    parent_id: categoryData.id
                };
                
                try {
                    const response = await dispatch(categoryActions.updateCategory(subcategory.id, updateData));
                    
                    if (response.success) {
                        // Reload subcategories
                        await loadSubcategories(categoryData.id);
                        
                        Swal.fire({
                            icon: 'success',
                            title: 'Updated!',
                            text: 'Subcategory updated successfully',
                            confirmButtonColor: '#00ff00',
                            timer: 1500
                        });
                    }
                } catch (error) {
                    console.error('Error updating subcategory:', error);
                }
            }
        });
    };

    // Delete subcategory
    const handleDeleteSubcategory = (subcategory) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `Delete subcategory "${subcategory.category_name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await dispatch(categoryActions.deleteCategory(subcategory.id));
                    
                    if (response.success) {
                        // Reload subcategories
                        await loadSubcategories(categoryData.id);
                        
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted!',
                            text: 'Subcategory deleted successfully',
                            confirmButtonColor: '#00ff00',
                            timer: 1500
                        });
                    }
                } catch (error) {
                    console.error('Error deleting subcategory:', error);
                }
            }
        });
    };

    const handleClose = () => {
        setCategoryData({
            id: '',
            category_name: '',
            description: '',
            parent_id: null,
            status: 'Active'
        });
        setSubcategories([]);
        setNewSubcategory({
            category_name: '',
            description: '',
            status: 'Active'
        });
        setShowSubcategoryForm(false);
        setErrors({});
        setIsModalOpen(false);
    };

    // Handle modal show event
    useEffect(() => {
        const modal = document.getElementById('edit-category');
        
        const handleShow = () => {
            if (selectedCategory && selectedCategory.id) {
                setCategoryData({
                    id: selectedCategory.id,
                    category_name: selectedCategory.category_name || selectedCategory.Brand || '',
                    description: selectedCategory.description || '',
                    parent_id: selectedCategory.parent_id || null,
                    status: selectedCategory.status || selectedCategory.Status || 'Active'
                });
                setIsModalOpen(true);
            }
        };
        
        if (modal) {
            modal.addEventListener('show.bs.modal', handleShow);
        }
        
        return () => {
            if (modal) {
                modal.removeEventListener('show.bs.modal', handleShow);
            }
        };
    }, [selectedCategory]);

    // Handle modal close event
    useEffect(() => {
        const modal = document.getElementById('edit-category');
        
        const handleHidden = () => {
            handleClose();
        };
        
        if (modal) {
            modal.addEventListener('hidden.bs.modal', handleHidden);
        }
        
        return () => {
            if (modal) {
                modal.removeEventListener('hidden.bs.modal', handleHidden);
            }
        };
    }, []);

    return (
        <div className="modal fade" id="edit-category">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header border-0 custom-modal-header">
                        <div className="page-title">
                            <h4>Edit Category</h4>
                            <h6 className="text-muted">{categoryData.category_name}</h6>
                        </div>
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                            id="closeEditCategoryModal"
                            aria-label="Close"
                            onClick={handleClose}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    
                    {isModalOpen ? (
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <label>Category Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.category_name ? 'is-invalid' : ''}`}
                                                name="category_name"
                                                value={categoryData.category_name}
                                                onChange={handleInputChange}
                                                placeholder="Enter category name"
                                                disabled={loading}
                                                autoFocus
                                            />
                                            {errors.category_name && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.category_name}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="col-lg-12 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <label>Parent Category</label>
                                            <Select
                                                className={`react-select ${errors.parent_id ? 'is-invalid' : ''}`}
                                                classNamePrefix="select"
                                                options={parentCategories}
                                                placeholder="Select parent category (optional)"
                                                value={parentCategories.find(opt => opt.value === categoryData.parent_id)}
                                                onChange={handleParentChange}
                                                isClearable={true}
                                                isDisabled={loading}
                                            />
                                            {errors.parent_id && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.parent_id}
                                                </div>
                                            )}
                                            <small className="text-muted">
                                                {categoryData.parent_id === null ? 
                                                    'This is a main category' : 
                                                    'This is a subcategory'}
                                            </small>
                                        </div>
                                    </div>
                                    
                                    <div className="col-lg-12 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <label>Description</label>
                                            <textarea
                                                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                                name="description"
                                                value={categoryData.description}
                                                onChange={handleInputChange}
                                                placeholder="Enter category description"
                                                rows="3"
                                                disabled={loading}
                                            />
                                            {errors.description && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.description}
                                                </div>
                                            )}
                                            <small className="text-muted">
                                                {categoryData.description.length}/500 characters
                                            </small>
                                        </div>
                                    </div>
                                    
                                    <div className="col-lg-12 col-sm-6 col-12">
                                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                                            <span className="status-label">Status</span>
                                            <input
                                                type="checkbox"
                                                id="edit_status"
                                                className="check"
                                                checked={categoryData.status === 'Active'}
                                                onChange={handleStatusChange}
                                                disabled={loading}
                                            />
                                            <label htmlFor="edit_status" className="checktoggle">
                                                {categoryData.status}
                                            </label>
                                        </div>
                                    </div>

                                    {/* Subcategories Section */}
                                    {categoryData.parent_id === null && (
                                        <div className="col-lg-12 mt-4">
                                            <div className="card border">
                                                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                                    <h6 className="mb-0">
                                                        <i className="fas fa-list me-2"></i>
                                                        Subcategories
                                                        {loadingSubcategories && (
                                                            <span className="spinner-border spinner-border-sm ms-2" role="status"></span>
                                                        )}
                                                    </h6>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => setShowSubcategoryForm(!showSubcategoryForm)}
                                                    >
                                                        <i className="fas fa-plus me-1"></i>
                                                        Add Subcategory
                                                    </button>
                                                </div>
                                                
                                                <div className="card-body">
                                                    {/* Add Subcategory Form */}
                                                    {showSubcategoryForm && (
                                                        <div className="add-subcategory-form mb-4 p-3 border rounded bg-light">
                                                            <h6 className="mb-3">Add New Subcategory</h6>
                                                            <div className="row">
                                                                <div className="col-lg-6">
                                                                    <div className="mb-3">
                                                                        <label>Subcategory Name *</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            name="category_name"
                                                                            value={newSubcategory.category_name}
                                                                            onChange={handleSubcategoryInputChange}
                                                                            placeholder="Enter subcategory name"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-6">
                                                                    <div className="mb-3">
                                                                        <label>Status</label>
                                                                        <div className="d-flex align-items-center">
                                                                            <input
                                                                                type="checkbox"
                                                                                id="subcategory_status"
                                                                                className="check me-2"
                                                                                checked={newSubcategory.status === 'Active'}
                                                                                onChange={handleSubcategoryStatusChange}
                                                                            />
                                                                            <label htmlFor="subcategory_status" className="checktoggle mb-0">
                                                                                {newSubcategory.status}
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-12">
                                                                    <div className="mb-3">
                                                                        <label>Description</label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            name="description"
                                                                            value={newSubcategory.description}
                                                                            onChange={handleSubcategoryInputChange}
                                                                            placeholder="Enter description"
                                                                            rows="2"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-12">
                                                                    <div className="d-flex gap-2">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-success"
                                                                            onClick={handleAddSubcategory}
                                                                        >
                                                                            <i className="fas fa-save me-1"></i>
                                                                            Save Subcategory
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-secondary"
                                                                            onClick={() => setShowSubcategoryForm(false)}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Subcategories List */}
                                                    {subcategories.length > 0 ? (
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-hover">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Name</th>
                                                                        <th>Status</th>
                                                                        <th>Description</th>
                                                                        <th className="text-end">Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {subcategories.map((sub) => (
                                                                        <tr key={sub.id}>
                                                                            <td>
                                                                                <strong>{sub.category_name}</strong>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge ${sub.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>
                                                                                    {sub.status}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <small className="text-muted">
                                                                                    {sub.description || '-'}
                                                                                </small>
                                                                            </td>
                                                                            <td className="text-end">
                                                                                <div className="btn-group btn-group-sm">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-outline-primary"
                                                                                        onClick={() => handleEditSubcategory(sub)}
                                                                                    >
                                                                                        <i className="fas fa-edit"></i>
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-outline-danger"
                                                                                        onClick={() => handleDeleteSubcategory(sub)}
                                                                                    >
                                                                                        <i className="fas fa-trash"></i>
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-3">
                                                            <i className="fas fa-folder-open fa-2x text-muted mb-2"></i>
                                                            <p className="text-muted mb-0">No subcategories found</p>
                                                            <small>Add your first subcategory using the button above</small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="modal-footer-btn mt-4">
                                    <button 
                                        type="button" 
                                        className="btn btn-cancel me-2" 
                                        data-bs-dismiss="modal"
                                        onClick={handleClose}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-submit" 
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            'Update Category'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <div className="modal-body text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading category details...</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

EditCategory.propTypes = {
    onSuccess: PropTypes.func.isRequired,
};

export default EditCategory;